import {
  PermissionsForMember,
  getSitePermissionsByRole,
  type SitePermissions,
  type SiteRolesType,
  type SimulatableRolesType,
  getWorkspacePermissionsByRole,
  type WorkspacePermissions,
  type WorkspaceRolesType,
} from '@packages/domains/authorization/permissions/catalog';
import {SITE_MEMBERS_PERMISSIONS_FETCH_SUCCEEDED} from '@packages/systems/permissions';
import {PermissionsAction} from './actions';

type PermissionsStoreConfig = {
  [key: string]: (action: any, state: PermissionsState) => PermissionsState;
};

// Opaque type removed - see /packages/utilities/type-safety/README.md for more information
export type PermissionsState = {
  hasLoaded?: boolean;
  site: SitePermissions;
  workspace: WorkspacePermissions;
  simulatedSiteRole: SimulatableRolesType | null; // the role being simulated
  // The user's saved default simulated role for this site. When set, the
  // designer auto-applies it on canvas load if no `simulateRole` URL param is
  // present.
  defaultSimulatedSiteRole: SimulatableRolesType | null;
};

export type MembersPermissionsState = {
  hasLoaded?: boolean;
  membersPermissions: PermissionsForMember[];
};

export const defaultMembersPermissionsState: MembersPermissionsState = {
  hasLoaded: false,
  membersPermissions: [],
};

export const defaultPermissionsState: PermissionsState = {
  hasLoaded: false,
  site: getSitePermissionsByRole('public'),
  workspace: getWorkspacePermissionsByRole('public'),
  simulatedSiteRole: null,
  defaultSimulatedSiteRole: null,
};

export const membersPermissionsReducer = (
  state: MembersPermissionsState = defaultMembersPermissionsState,
  action: PermissionsAction
): MembersPermissionsState => {
  switch (action.type) {
    case SITE_MEMBERS_PERMISSIONS_FETCH_SUCCEEDED: {
      const {permissions} = action.payload.response;
      return {
        ...state,
        hasLoaded: true,
        membersPermissions: permissions,
      };
    }
  }
  return state;
};

/**
 * This implementation allows us to write the update logic here, but
 * keep the knowledge of action data structure and action types out
 * of the package. In future, we'll probably need to extend the
 * config so that we can trigger different logic internally,
 * or pass through custom permissions.
 *
 * Site permissions and workspace in the backend are combined into one
 * object so it fine if site and workspace below share the same permission
 * the action resources themselves are unique.
 **/
export function createPermissionsReducer(config: PermissionsStoreConfig) {
  return function permissionsReducer(
    state: PermissionsState = defaultPermissionsState,
    action: any
  ): PermissionsState {
    const reducerActionToHandle = config[action.type];
    if (reducerActionToHandle) {
      return reducerActionToHandle(action, state);
    }

    return state;
  };
}

export const getTestPermissionsState = ({
  site,
  workspace,
  simulatedSiteRole,
  defaultSimulatedSiteRole,
}: {
  site: SiteRolesType;
  workspace: WorkspaceRolesType;
  simulatedSiteRole: SimulatableRolesType | null;
  defaultSimulatedSiteRole: SimulatableRolesType | null;
}) => ({
  site: getSitePermissionsByRole(site),
  workspace: getWorkspacePermissionsByRole(workspace),
  simulatedSiteRole,
  defaultSimulatedSiteRole,
});

type SiteResource = keyof SitePermissions;
type WorkspaceResource = keyof WorkspacePermissions;

type SiteResourceAction<R extends SiteResource> = keyof SitePermissions[R];
type WorkspaceResourceAction<R extends WorkspaceResource> =
  keyof WorkspacePermissions[R];

type SitePermissionSelector = (
  action: string,
  resource: SiteResource
) => (state: PermissionsState) => boolean;

type WorkspacePermissionSelector = (
  action: string,
  resource: WorkspaceResource
) => (state: PermissionsState) => boolean;

/**
 * Gets a permission value from resource permissions, checking if the action exists first
 */
function getPermissionValue(
  resourcePermissions: SitePermissions[SiteResource] | undefined,
  action: string
): boolean {
  if (!resourcePermissions) {
    return false;
  }
  // Check if action exists as a key
  if (action in resourcePermissions) {
    return (
      resourcePermissions[action as SiteResourceAction<SiteResource>] ?? false
    );
  }
  return false;
}

export const getSitePermission: SitePermissionSelector =
  (action, resource) => (state: PermissionsState) => {
    if (state.simulatedSiteRole) {
      if (resource === 'site' && action === 'simulateRoles') {
        return getPermissionValue(state.site[resource], action);
      }

      // Otherwise, get permissions for the simulated role
      const simulatedPermissions = getSitePermissionsByRole(
        state.simulatedSiteRole
      );
      return getPermissionValue(simulatedPermissions[resource], action);
    }

    return getPermissionValue(state.site[resource], action);
  };

export const getWorkspacePermission: WorkspacePermissionSelector =
  (action, resource) => (state: PermissionsState) =>
    state.workspace[resource]?.[
      action as WorkspaceResourceAction<WorkspaceResource>
    ] ?? false;
